import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { ThemeProvider } from '@/components/feedback/ThemeProvider'
import { ToastProvider } from '@/components/feedback'
import { Navbar } from '@/components/navigation'
import { SearchModal } from '@/components/common/SearchModal'
import { ScrollToTop } from '@/components/common/ScrollToTop'
import { useSearchShortcut } from '@/hooks/useSearchShortcut'
import { ReferralLandingHandler } from '@/components/referral/ReferralLandingHandler'
import { ReferralLinkRedirect } from '@/components/referral/ReferralLinkRedirect'

const Home = lazy(() => import('@/pages/Home'))
const Courses = lazy(() => import('@/pages/Courses'))
const CourseOverview = lazy(() => import('@/pages/CourseOverview'))
const Lesson = lazy(() => import('@/pages/Lesson'))
const Quiz = lazy(() => import('@/pages/Quiz'))
const Glossary = lazy(() => import('@/pages/Glossary'))
const Resources = lazy(() => import('@/pages/Resources'))
const Profile = lazy(() => import('@/pages/Profile'))
const Settings = lazy(() => import('@/pages/Settings'))
const Roadmap = lazy(() => import('@/pages/Roadmap'))
const Certificates = lazy(() => import('@/pages/Certificates'))
const Pricing = lazy(() => import('@/pages/Pricing'))
const Login = lazy(() => import('@/pages/Login'))
const Callback = lazy(() => import('@/pages/Callback'))
const MyLearning = lazy(() => import('@/pages/MyLearning'))
const Admin = lazy(() => import('@/pages/Admin'))
const AdminInfluencers = lazy(() => import('@/pages/AdminInfluencers'))
const AdminInfluencerDetail = lazy(
  () => import('@/components/admin/influencers/InfluencerDetailPage'),
)
const InfluencerDashboard = lazy(() => import('@/pages/InfluencerDashboard'))
const InfluencerNotRegistered = lazy(() => import('@/pages/InfluencerNotRegistered'))
const CreatorProgram = lazy(() => import('@/pages/CreatorProgram'))
const About = lazy(() => import('@/pages/About'))
const Disclaimer = lazy(() => import('@/pages/Disclaimer'))
const Terms = lazy(() => import('@/pages/Terms'))
const Privacy = lazy(() => import('@/pages/Privacy'))
const NotFound = lazy(() => import('@/pages/NotFound'))

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-3 border-[var(--color-brand-400)] border-t-transparent rounded-full animate-spin" />
        <p className="text-[var(--color-text-secondary)] text-sm">Loading...</p>
      </div>
    </div>
  )
}

function AppContent() {
  const { open: searchOpen, setOpen: setSearchOpen } = useSearchShortcut()

  return (
    <>
      <ScrollToTop />
      {/* Handles ?ref=CODE / ?code=CODE on every page — must be inside the
          router so it can read the current location, and inside ToastProvider
          so it can surface the "You're using X's referral!" confirmation. */}
      <ReferralLandingHandler />
      <a href="#main-content" className="skip-to-content">
        Skip to content
      </a>
      <Navbar />
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="/callback/r/:rp" element={<Callback />} />
          {/* Short referral links — /r/:slug resolves server-side to a destination URL. */}
          <Route path="/r/:slug" element={<ReferralLinkRedirect />} />
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/my-learning" element={<MyLearning />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/influencers" element={<AdminInfluencers />} />
          <Route path="/admin/influencers/:id" element={<AdminInfluencerDetail />} />
          {/* Influencer dashboard — one component handles all sub-sections
              based on the URL; layout+guard live inside. */}
          <Route path="/influencer" element={<InfluencerDashboard />} />
          <Route path="/influencer/dashboard" element={<InfluencerDashboard />} />
          <Route path="/influencer/codes" element={<InfluencerDashboard />} />
          <Route path="/influencer/conversions" element={<InfluencerDashboard />} />
          <Route path="/influencer/payouts" element={<InfluencerDashboard />} />
          <Route path="/influencer/marketing-kit" element={<InfluencerDashboard />} />
          <Route path="/influencer/profile" element={<InfluencerDashboard />} />
          <Route path="/influencer/not-registered" element={<InfluencerNotRegistered />} />
          {/* Public creator marketing pages. */}
          <Route path="/creators" element={<CreatorProgram />} />
          <Route path="/creator-program" element={<CreatorProgram />} />
          <Route path="/courses/:courseId" element={<CourseOverview />} />
          <Route path="/courses/:courseId/:moduleId" element={<Lesson />} />
          <Route path="/courses/:courseId/:moduleId/quiz" element={<Quiz />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/search" element={<Home />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/about" element={<About />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}
